<template>
  <section class="section-base form-section-base">
    <v-container>
      <Proxy
        ref="proxies"
        v-for="(v, i) in $v.proxies.$each.$iter"
        :v="v"
        :key="`proxies ${i}`"
        :can-delete="$v.proxies.$model.length > 1"
        @add-proxy-person="addProxyPerson(i)"
        @add-proxy-trustee="addProxyTrustee(i)"
        @delete-proxy="deleteProxy(i)"
        @delete-proxy-person="deleteProxyPerson(i, $event)"
        @delete-proxy-trustee="deleteProxyTrustee(i, $event)"
      />

      <Footer
        @next-btn-click="btnSaveClickHandler(StepDirection.NEXT)"
        @prev-btn-click="btnSaveClickHandler(StepDirection.PREV)"
        :loading-next="loadingNext"
      />
    </v-container>
  </section>
</template>

<script>
import cloneDeep from 'clone-deep';
import deepmerge from 'deepmerge';

import { leaveStep } from '@/utils/services';
import Footer from '@/components/services/elements/Footer';
import { StepDirection, StepName } from '@/types/services';
import Proxy, { proxyModel, proxyValidation } from './Proxy';
import { proxyTrusteeModel } from './ProxyTrustee/ProxyTrustee';
import { proxyPersonModel } from './ProxyPerson/ProxyPerson';
import { ServicesModule } from '@/store/modules/services';
import { Role } from '@/utils/dictionaries';
import { PROXY_DATE_TYPE, RodStep } from '@/constants';

export default {
  name: 'StepProxy',
  props: {
    loadingNext: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    Footer,
    Proxy,
  },
  data() {
    return {
      StepDirection,
      StepName,
      leaveStep,
      proxies: [cloneDeep(proxyModel)],
      invalidFields: [],
    };
  },
  validations: {
    proxies: {
      $each: proxyValidation,
    },
  },
  created() {
    this.prefillDraft();
  },
  methods: {
    addProxy() {
      this.proxies.push(
        deepmerge(proxyModel, {
          number: this.proxies.length + 1,
        })
      );
    },

    addProxyPerson(index) {
      this.proxies[index].proxyPersons.push(
        deepmerge(proxyPersonModel, {
          number: this.proxies[index].proxyPersons.length + 1,
        })
      );
    },

    addProxyTrustee(i) {
      this.proxies[i].trusties.push(
        deepmerge(proxyTrusteeModel, {
          number: this.proxies[i].trusties.length + 1,
        })
      );
    },

    deleteProxy(i) {
      this.proxies.splice(i, 1);
      this.updateProxiesNumber();
    },

    deleteProxyPerson(proxyI, proxyPersonI) {
      this.proxies[proxyI].proxyPersons.splice(proxyPersonI, 1);
      this.updateProxyPersonsNumber(proxyI);
    },

    deleteProxyTrustee(proxyI, proxyTrusteeI) {
      this.proxies[proxyI].trusties.splice(proxyTrusteeI, 1);
      this.updateProxyTrustiesNumber(proxyI);
    },

    updateProxiesNumber() {
      this.proxies.forEach((proxy, i) => {
        proxy.number = i + 1;
      });
    },

    updateProxyPersonsNumber(proxyI) {
      this.proxies[proxyI].proxyPersons.forEach((proxyPerson, i) => {
        proxyPerson.number = i + 1;
      });
    },

    updateProxyTrustiesNumber(proxyI) {
      this.proxies[proxyI].trusties.forEach((proxyTrustee, i) => {
        proxyTrustee.number = i + 1;
      });
    },

    prefillDraft() {
      if (!ServicesModule.statement.rodDetails?.proxies?.length) {
        return;
      }

      const proxiesStore = cloneDeep(
        ServicesModule.statement.rodDetails.proxies
      );

      this.proxies = proxiesStore.map((proxy) =>
        this.prefillProxy(proxy, false)
      );
    },

    prefillProxy(proxy) {
      proxy.trusties = this.prefillProxyEntities({
        proxyStore: proxy,
        entitiesName: 'trusties',
      });
      proxy.proxyPersons = this.prefillProxyEntities({
        proxyStore: proxy,
        entitiesName: 'proxyPersons',
      });
      proxy.dateType = proxy.issuePeriodFrom
        ? PROXY_DATE_TYPE.Period
        : PROXY_DATE_TYPE.Single;

      const cleanProxyModel = cloneDeep(proxyModel);

      cleanProxyModel.trusties = [];
      cleanProxyModel.proxyPersons = [];

      return deepmerge.all([cleanProxyModel, proxy]);
    },

    prefillProxyEntities({ proxyStore, entitiesName }) {
      return proxyStore[entitiesName].map((entityStore) => {
        entityStore.role = Role.PERSON;
        if (entityStore.organization) entityStore.role = Role.ORGANIZATION;
        if (entityStore.foreignOrganization) {
          entityStore.role = Role.FOREIGN_ORGANIZATION;
        }

        if (entityStore.identityDocument) {
          entityStore.person.identityDocument = entityStore.identityDocument;
        }

        return deepmerge(
          entitiesName === 'trusties' ? proxyTrusteeModel : proxyPersonModel,
          entityStore
        );
      });
    },

    assemblyModel() {
      return this.proxies.map((proxy) => {
        const trusties = this.assemblyProxyEntities({
          proxy,
          entitiesName: 'trusties',
        });
        const proxyPersons = this.assemblyProxyEntities({
          proxy,
          entitiesName: 'proxyPersons',
        });
        const issueDate =
          proxy.dateType === PROXY_DATE_TYPE.Single ? proxy.issueDate : null;
        const issuePeriod =
          proxy.dateType === PROXY_DATE_TYPE.Period ? proxy.issuePeriod : null;

        return {
          ...proxy,
          issueDate,
          issuePeriod,
          trusties,
          proxyPersons,
        };
      });
    },

    assemblyProxyEntities({ proxy, entitiesName }) {
      return proxy[entitiesName].map((entity) => {
        const role = entity.role;

        return {
          number: entity.number,
          [role]: entity[role],
          identityDocument:
            role === Role.PERSON ? entity.person.identityDocument : null,
        };
      });
    },

    async touchFields() {
      this.invalidFields = [];

      proxyLoop: for (const [proxyI, proxy] of Object.entries(
        this.$v.proxies.$each.$iter
      )) {
        if (!proxy.$invalid) continue;

        const proxyRef = this.$refs.proxies[proxyI];

        // Проверям первую вкладку
        for (const fieldName of [
          'issueDate',
          'issuePeriodFrom',
          'issueNumber',
          'summary',
        ]) {
          if (proxy[fieldName].$invalid) {
            this.invalidFields.push(true);
            proxyRef.tab = 0;
            continue proxyLoop;
          }
        }

        // Проверям вторую вкладку
        let secondTabInvalid = false;
        for (const [proxyTrusteeI, proxyTrustee] of Object.entries(
          proxy.trusties.$each.$iter
        )) {
          const proxyTrusteeRef = proxyRef.$refs.trusties[proxyTrusteeI];
          const role = proxyTrustee.role.$model;

          if (proxyTrustee[role].$invalid) {
            this.invalidFields.push(true);
            proxyTrusteeRef.expand = true;
            secondTabInvalid = true;
          }
        }

        if (secondTabInvalid) {
          proxyRef.tab = 1;
          continue;
        }

        // Проверяем третью вкладку
        let thirdTabInvalid = false;
        for (const [proxyPersonI, proxyPerson] of Object.entries(
          proxy.proxyPersons.$each.$iter
        )) {
          const proxyPersonRef = proxyRef.$refs.proxyPersons[proxyPersonI];
          const role = proxyPerson.role.$model;

          if (proxyPerson[role].$invalid) {
            this.invalidFields.push(true);
            proxyPersonRef.expand = true;
            thirdTabInvalid = true;
          }
        }

        if (thirdTabInvalid) {
          proxyRef.tab = 2;
        }
      }
      this.$v.proxies.$touch();
    },

    async btnSaveClickHandler(direction) {
      leaveStep({
        stepThis: this,
        direction,
        step: RodStep.PROXIES,
      });
    },
  },
};
</script>
